import { memo, type ReactNode } from 'react';

import {
    Button as AntButton,
    type ButtonProps as AntButtonProps,
    Tooltip,
} from 'antd';

import { useMobileMediaQuery } from '@/shared/lib/hooks';

import cls from './button.module.css';

export interface ButtonProps extends Omit<AntButtonProps, 'type' | 'children'> {
    variant?:
        | 'primary'
        | 'secondary'
        | 'tertiary'
        | 'ghost'
        | 'danger'
        | 'wiggle';
    title?: string;
    mobileLabel?: string;
    icon?: ReactNode;
    iconPosition?: 'start' | 'end';
    isLoading?: boolean;
    isFullWidth?: boolean | undefined;
    disabledTooltipText?: string;
}

export const Button = memo((props: ButtonProps) => {
    const {
        variant = 'primary',
        className,
        icon,
        title,
        mobileLabel,
        iconPosition = 'start',
        isLoading = false,
        isFullWidth = false,
        disabledTooltipText,
        ...otherProps
    } = props;

    const isMobile = useMobileMediaQuery();
    const displayLabel = isMobile && mobileLabel ? mobileLabel : title;

    const classNames = [
        cls.button,
        cls[variant],
        displayLabel ? cls.withTitle : null,
        icon && !displayLabel ? cls.iconOnly : null,
        className,
        isFullWidth ? cls.fullWidth : null,
    ]
        .filter(Boolean)
        .join(' ');

    const button = (
        <AntButton
            className={classNames}
            icon={icon}
            iconPosition={iconPosition}
            loading={isLoading}
            title={displayLabel}
            type="text"
            {...otherProps}
        >
            {displayLabel}
        </AntButton>
    );

    if (props.disabled && disabledTooltipText) {
        return <Tooltip title={disabledTooltipText}>{button}</Tooltip>;
    }

    return button;
});

Button.displayName = 'Button';
